import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ContainerForm,
  AInput,
  ASelect,
  ASwitch,
  OptionFooter,
  PageIntro,
  PageLayout,
  PagePanel,
} from '../../../components';
import { DollarOutlined, FileTextOutlined } from '@ant-design/icons';
import { Col, Form, Row, Card, Divider } from 'antd';
import api from '../../../services/api';
import {
  IClient,
  IFinancialPlan,
  IFinancial,
  IPaymentMethod,
} from '../../../shared/interfaces';
import { useToast } from '../../../hooks/toast';
import { schemaFinancial } from '../schemas/yup.financial.schema';
import {
  getSelectClients,
  getSelectFinancialPlans,
  getSelectPaymentsMethods,
} from '../../../http';
import { ATextArea } from '../../../components';
import {
  formatToBRL,
  parseCurrencyInput,
} from '../../../shared/utils/functions';
import { useI18n } from '../../../shared/i18n';

interface IResponseFinancials {
  data: IFinancial;
}

const installments = Array.from({ length: 24 }, (_, i) => ({
  label: `${i + 1}x`,
  value: i + 1,
}));

const FinancialCreateEdit: React.FC = () => {
  const { id } = useParams();
  const { addToast } = useToast();
  const navigate = useNavigate();
  const { t } = useI18n();

  // Data Lists
  const [clients, setClients] = useState<IClient[]>([]);
  const [financialPlans, setFinancialPlans] = useState<IFinancialPlan[]>([]);
  const [payments, setPayments] = useState<IPaymentMethod[]>([]);

  // Form State
  const [code, setCode] = useState<number>();
  const [due_date, setDue_date] = useState<string>();
  const [financial_plan_id, setFinancial_plan_id] = useState<string | null>();
  const [client_id, setClient_id] = useState<string>();
  const [description, setDescription] = useState<string>();
  const [observation, setObservation] = useState<string | null>();
  const [value, setValue] = useState<number>(0);
  const [type, setType] = useState<number>(1);
  const [number_doc, setNumber_doc] = useState<string>();
  const [quantity_installments, setQuantity_installments] = useState<number>(1);
  const [repeat_value, setRepeat_value] = useState<boolean>(false);
  const [payment, setPayment] = useState<string>();
  const [disabled, setDisabled] = useState<boolean>(false);

  const loadData = useCallback(async () => {
    if (id === 'new') return;
    try {
      const response = await api.get(`/financials/${id}`);
      const { data }: IResponseFinancials = response;
      setCode(data.code);
      setClient_id(data.client_id);
      setType(data.type);
      setDescription(data.description);
      setFinancial_plan_id(data.financial_plan_id);
      setDue_date(data.due_date);
      setValue(data.value);
      setObservation(data.observation);
      setNumber_doc(data.number_doc);
      setPayment(data.payment);
    } catch (error) {
      addToast({
        type: 'error',
        title: t('financialsPage.createEdit.toast.errorTitle'),
        description: t('financialsPage.createEdit.toast.loadDataError'),
      });
    }
  }, [id, addToast, t]);

  const loadDependencies = useCallback(async () => {
    try {
      const [plansData, paymentsData] = await Promise.all([
        getSelectFinancialPlans(),
        getSelectPaymentsMethods(),
      ]);
      setFinancialPlans(plansData);
      setPayments(paymentsData);
    } catch (error) {
      addToast({
        type: 'error',
        title: t('financialsPage.createEdit.toast.errorTitle'),
        description: t('financialsPage.createEdit.toast.loadDependenciesError'),
      });
    }
  }, [addToast, t]);

  const loadClients = useCallback(async () => {
    setClients([]);
    try {
      const data = await getSelectClients(type);
      setClients(data);
    } catch (error: any) {
      addToast({
        type: 'error',
        title: 'Erro',
        description: error.response?.data?.error,
      });
    }
  }, [type, addToast]);

  const handleSubmit = useCallback(async () => {
    setDisabled(true);
    const formData = {
      due_date,
      financial_plan_id,
      client_id,
      description,
      observation,
      value,
      type,
      number_doc,
      payment,
      issue_date:
        id === 'new' ? new Date().toLocaleDateString('fr-CA') : undefined,
    };

    schemaFinancial
      .validate(formData)
      .then(async (validated) => {
        try {
          if (id === 'new') {
            await api.post('/financials', {
              ...validated,
              quantity_installments,
              repeat_value,
            });
            addToast({
              type: 'success',
              title: t('financialsPage.createEdit.toast.successTitle'),
              description: t('financialsPage.createEdit.toast.created'),
            });
            setTimeout(() => navigate(-1), 1000);
          } else {
            await api.patch(`/financials/${id}`, {
              ...validated,
            });
            addToast({
              type: 'success',
              title: t('financialsPage.createEdit.toast.successTitle'),
              description: t('financialsPage.createEdit.toast.updated'),
            });
            setDisabled(false);
          }
        } catch (error: any) {
          addToast({
            type: 'error',
            title: t('financialsPage.createEdit.toast.errorTitle'),
            description: error.response?.data?.message,
          });
          setDisabled(false);
        }
      })
      .catch((err) => {
        addToast({
          type: 'warning',
          title: t('financialsPage.createEdit.toast.warningTitle'),
          description:
            err.errors?.[0] || t('financialsPage.createEdit.toast.checkFields'),
        });
        setDisabled(false);
      });
  }, [
    due_date,
    financial_plan_id,
    client_id,
    description,
    observation,
    value,
    type,
    number_doc,
    payment,
    id,
    quantity_installments,
    repeat_value,
    addToast,
    navigate,
    t,
  ]);

  const handlePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const numericValue = parseCurrencyInput(e.target.value);
    setValue(numericValue);
  };

  useEffect(() => {
    loadData();
    loadDependencies();
  }, [loadData, loadDependencies]);

  useEffect(() => {
    loadClients();
  }, [loadClients]);

  return (
    <PageLayout>
      <PageIntro
        badge={t('financialsPage.intro.badge')}
        icon={<DollarOutlined />}
        title={
          id === 'new'
            ? t('financialsPage.createEdit.titleCreate')
            : t('financialsPage.createEdit.titleEdit', { code: code ?? '' })
        }
        description={t('financialsPage.createEdit.description')}
      />
      <PagePanel>
        <ContainerForm>
          <Form layout="vertical" style={{ width: '100%', maxWidth: '1200px' }}>
            <Row gutter={[24, 24]}>
              <Col xs={24} lg={16}>
                <Card
                  title={t('financialsPage.createEdit.sections.entryInfo')}
                  bordered={false}
                  className="shadow-sm"
                >
                  <Row gutter={16}>
                    <Col span={8}>
                      <Form.Item
                        label={t(
                          'financialsPage.createEdit.fields.movementType',
                        )}
                        required
                      >
                        <ASelect
                          value={type}
                          onChange={setType}
                          options={[
                            {
                              label: t(
                                'financialsPage.createEdit.movementTypes.income',
                              ),
                              value: 1,
                            },
                            {
                              label: t(
                                'financialsPage.createEdit.movementTypes.expense',
                              ),
                              value: 0,
                            },
                          ]}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={16}>
                      <Form.Item
                        label={t(
                          'financialsPage.createEdit.fields.description',
                        )}
                        required
                      >
                        <AInput
                          placeholder={t(
                            'financialsPage.createEdit.fields.descriptionPlaceholder',
                          )}
                          value={description}
                          onChange={(e) => setDescription(e.target.value)}
                          prefix={<FileTextOutlined />}
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        label={
                          type === 1
                            ? t('financialsPage.createEdit.fields.client')
                            : t('financialsPage.createEdit.fields.supplier')
                        }
                        required
                      >
                        <ASelect
                          showSearch
                          placeholder={t(
                            'financialsPage.createEdit.fields.selectPlaceholder',
                          )}
                          optionFilterProp="label"
                          onChange={setClient_id}
                          value={client_id}
                          options={clients.map((c) => ({
                            label: c.corporate_name,
                            value: c.id,
                          }))}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        label={t(
                          'financialsPage.createEdit.fields.accountPlan',
                        )}
                      >
                        <ASelect
                          showSearch
                          placeholder={t(
                            'financialsPage.createEdit.fields.accountPlanPlaceholder',
                          )}
                          optionFilterProp="label"
                          onChange={setFinancial_plan_id}
                          value={financial_plan_id}
                          allowClear
                          options={financialPlans.map((fp) => ({
                            label: fp.description,
                            value: fp.id,
                          }))}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </Card>

                <Card
                  title={t(
                    'financialsPage.createEdit.sections.financialDetails',
                  )}
                  bordered={false}
                  className="shadow-sm"
                  style={{ marginTop: 24 }}
                >
                  <Row gutter={16}>
                    <Col span={8}>
                      <Form.Item
                        label={t('financialsPage.createEdit.fields.value')}
                        required
                      >
                        <AInput
                          value={formatToBRL(value)}
                          onChange={handlePriceChange}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item
                        label={t('financialsPage.createEdit.fields.dueDate')}
                        required
                      >
                        <AInput
                          value={due_date}
                          type="date"
                          onChange={(e) => setDue_date(e.target.value)}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item
                        label={t(
                          'financialsPage.createEdit.fields.paymentMethod',
                        )}
                      >
                        <ASelect
                          value={payment}
                          placeholder={t(
                            'financialsPage.createEdit.fields.selectPlaceholder',
                          )}
                          onChange={setPayment}
                          allowClear
                          options={payments.map((p) => ({
                            label: p.description,
                            value: p.id,
                          }))}
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={16}>
                    <Col span={8}>
                      <Form.Item
                        label={t(
                          'financialsPage.createEdit.fields.documentNumber',
                        )}
                        required
                      >
                        <AInput
                          value={number_doc || ''}
                          onChange={(e) => setNumber_doc(e.target.value)}
                        />
                      </Form.Item>
                    </Col>
                    {id === 'new' && (
                      <>
                        <Col span={8}>
                          <Form.Item
                            label={t(
                              'financialsPage.createEdit.fields.installments',
                            )}
                          >
                            <ASelect
                              value={quantity_installments}
                              onChange={setQuantity_installments}
                              options={installments}
                            />
                          </Form.Item>
                        </Col>
                        <Col span={8}>
                          <Form.Item
                            label={t(
                              'financialsPage.createEdit.fields.options',
                            )}
                          >
                            <ASwitch
                              checked={repeat_value}
                              onChange={() => setRepeat_value(!repeat_value)}
                              checkedChildren={t(
                                'financialsPage.createEdit.fields.repeatValue',
                              )}
                              unCheckedChildren={t(
                                'financialsPage.createEdit.fields.splitValue',
                              )}
                            />
                          </Form.Item>
                        </Col>
                      </>
                    )}
                  </Row>
                </Card>
              </Col>

              <Col xs={24} lg={8}>
                <Card
                  title={t('financialsPage.createEdit.sections.observations')}
                  bordered={false}
                  className="shadow-sm"
                  style={{ height: '100%' }}
                >
                  <Form.Item>
                    <ATextArea
                      rows={6}
                      placeholder={t(
                        'financialsPage.createEdit.fields.observationsPlaceholder',
                      )}
                      value={observation || ''}
                      onChange={(e) => setObservation(e.target.value)}
                    />
                  </Form.Item>
                </Card>
              </Col>
            </Row>

            <Divider />

            <OptionFooter
              onClickCancel={() => navigate(-1)}
              disabled={disabled}
              onClickOk={handleSubmit}
              okText={
                id === 'new'
                  ? t('financialsPage.createEdit.actions.create')
                  : t('financialsPage.createEdit.actions.save')
              }
            />
          </Form>
        </ContainerForm>
      </PagePanel>
    </PageLayout>
  );
};

export default FinancialCreateEdit;
